import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { editProduct } from "src/api/inventory";
import { NumberField, TextAreaField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { showSuccessToast } from "src/lib/utils";
import type { ProductRow } from "src/types/inventory";

const validationSchema = Yup.object().shape({
	name: Yup.string().required("This field is required"),
	sku: Yup.string().required("This field is required"),
	cost_price: Yup.string().required("This field is required"),
	price: Yup.string().required("This field is required"),
	description: Yup.string(),
});

interface EditProductFormValues {
	uuid: string;
	name: string;
	sku: string;
	cost_price: string;
	price: string;
	description: string;
	image: File | null;
}

interface EditProductModalProps {
	product: ProductRow;
}

const EditProductModal = ({ product }: EditProductModalProps) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();

	const initialValues: EditProductFormValues = {
		uuid: product.uuid,
		name: product.name || "",
		sku: product.sku || "",
		cost_price: String(product.cost_price ?? ""),
		price: String(product.price ?? ""),
		description: product.description || "",
		image: null,
	};

	const { mutate, isLoading } = useMutation(editProduct, {
		onSuccess: () => {
			showSuccessToast("Product updated successfully");
			queryClient.invalidateQueries(["inventory"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="Name"
							name="name"
							placeholder="Product name"
							value={values.name}
							error={touched.name && errors.name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="SKU"
							name="sku"
							placeholder="SKU"
							value={values.sku}
							error={touched.sku && errors.sku}
							onChange={handleChange}
							required
						/>
						<NumberField
							label="Cost price"
							name="cost_price"
							placeholder="0.00"
							value={values.cost_price}
							error={touched.cost_price && errors.cost_price}
							onChange={(value) => setFieldValue("cost_price", value)}
							required
						/>
						<NumberField
							label="Selling price"
							name="price"
							placeholder="0.00"
							value={values.price}
							error={touched.price && errors.price}
							onChange={(value) => setFieldValue("price", value)}
							required
						/>
					</div>

					<TextAreaField
						label="Description"
						name="description"
						placeholder="What is this product?"
						value={values.description}
						error={touched.description && errors.description}
						onChange={handleChange}
					/>

					<TextField
						label="Image"
						name="image"
						type="file"
						accept="image/*"
						hint="Leave empty to keep the current image"
						onChange={(event) => setFieldValue("image", event.currentTarget.files?.[0] ?? null)}
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Updating..." : "Update product"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default EditProductModal;
